import { useState, useEffect, useRef } from 'react';
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
// @mui
import {
  Stack,
  Typography,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// crop
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from 'react-image-crop';
// components
import Image from '../../../components/Image';
import Iconify from '../../../components/Iconify';

import 'react-image-crop/dist/ReactCrop.css'
import DIOText from '../../../components/DIOText';


// ----------------------------------------------------------------------

export default function FliersProfileLogoUploader({ originImageURL=null, updateLogo }) {
  const smUp = useResponsive('up', 'sm');
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [imageSrc, setImageSrc] = useState('');
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [openLogoDialog, setOpenLogoDialog] = useState(false);

  // blogImage
  const [fileBlob, setFileBlob] = useState(null);

  const onChangeFile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result?.toString() || '');
        setOpenLogoDialog(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const centerAspectCrop = ( mediaWidth, mediaHeight, aspect ) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    )
  }

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget
    const loadCrop = centerAspectCrop(width, height, 1);
    setCrop(loadCrop);
    setCompletedCrop(convertToPixelCrop(loadCrop, width, height));
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
        )
      }
    },
    100,
    [completedCrop],
  )

  const endCrop = (c) => {
    setCompletedCrop(c);
  };

  const setLogo = () => {
    if (completedCrop) {
      const canvas = previewCanvasRef.current;
      if (!canvas) {
        return;
      }
      canvas.toBlob((blob) => {
        updateLogo(blob);
        setFileBlob(URL.createObjectURL(blob));
        inputRef.current.value = '';
        setOpenLogoDialog(false);
      });
    }
  }

  const renderPreview = (
    <Image
      alt="logo"
      src={fileBlob || originImageURL || 'https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/fliers-no-logo-2x.png'}
      sx={{
        width: 128,
        height: 128,
        borderRadius: 3,
      }}
    />
  );

  const renderPlaceholder = (
    <Stack
      alignItems="center"
      justifyContent="center"
      className="upload-placeholder"
      sx={{
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        zIndex: 9,
        borderRadius: 2.5,
        position: 'absolute',
        opacity: 0,
        color: 'common.white',
        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
        transition: (theme) =>
          theme.transitions.create(['opacity'], {
            duration: theme.transitions.duration.shorter,
          }),
      }}
    >
      <Iconify icon="mdi:camera-plus" sx={{ width: 24, height: 24, mb: 0.5 }} />
      <Typography variant="caption">{'로고'}</Typography>
      <Typography variant="caption">{'이미지 업로드'}</Typography>
    </Stack>
  );

  const renderUploadButtonContent = (
    <Box
      sx={{
        width: 1,
        height: 1,
        overflow: 'hidden',
        borderRadius: 3,
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}
    >
      {renderPreview}
      {renderPlaceholder}
    </Box>
  );

  const logoUploadButton = (
    <Box
      sx={{
        p: 1,
        minWidth: 144,
        minHeight: 144,
        width: 144,
        height: 144,
        cursor: 'pointer',
        overflow: 'hidden',
        borderRadius: 3,
        border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
        '&:hover .upload-placeholder': {
          opacity: 1,
        },
      }}
      onClick={() => inputRef.current.click()}
    >
      {renderUploadButtonContent}
    </Box>
  );

  return (
    <>
    <input id="logo" type="file" accept="image/*" ref={inputRef} style={{ display: 'none' }} onChange={onChangeFile} />
    {logoUploadButton}
    <Dialog open={openLogoDialog} maxWidth="xs" onClose={() => setOpenLogoDialog(false)} fullWidth fullScreen={!smUp}>
      <DialogContent>
        <Box sx={{ mt: { xs: 2, sm: 0 }}}>
          <Typography variant="h5" gutterBottom>
            로고 이미지 자르기
          </Typography>
          <Typography variant="body2" paragraph>
            로고 이미지가 정사각형 안에 잘 보이게 설정해주세요.
          </Typography>
          <Box
            sx={{ backgroundColor: "grey.400", alignItems: 'center', justifyContent: 'center', display: 'flex' }}
          >
          {!!imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => { endCrop(c) }}
              aspect={1}
              minWidth={150}
              minHeight={150}
              keepSelection
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageSrc}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              미리보기
            </Typography>
            <Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <canvas
                  style={{
                    borderRadius: 16,
                    width: smUp ? 150 : 80,
                    height: smUp ? 150 : 80,
                  }}
                  ref={previewCanvasRef}
                  objectFit="contain"
                />
                <Stack spacing={1}>
                  <Typography variant={smUp ? "body2" : "caption"} sx={{ color: "text.secondary"}}>
                    - 로고 이미지는 가로, 세로 최소 720px 사이즈의 이미지 파일(jpeg, jpg, png)을 권장합니다.
                  </Typography>
                  <Typography variant={smUp ? "body2" : "caption"} sx={{ color: "text.secondary"}}>
                    - 저화질 로고 이미지 파일의 경우, <DIOText/>에서 이미지 파일을 재요청 할 수 있습니다.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button sx={{ px: 4 }} variant="text" size="large" onClick={() => setOpenLogoDialog(false)}>취소</Button>
            <Button sx={{ px: 4 }} variant="contained" size="large" onClick={setLogo}>선택하기</Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
    </>
  )
}

async function canvasPreview(
  image,
  canvas,
  crop,
) {
  if (!image || !canvas || !crop) {
    return
  }
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('No 2d context')
  }

  const scaleX = image.naturalWidth / image.width
  const scaleY = image.naturalHeight / image.height
  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = window.devicePixelRatio

  // canvas.width = Math.floor(crop.width * scaleX * pixelRatio)
  // canvas.height = Math.floor(crop.height * scaleY * pixelRatio)

  ctx.scale(pixelRatio, pixelRatio)
  ctx.imageSmoothingQuality = 'high'
  ctx.save()

  canvas.width = 150
  canvas.height = 150

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height,
  )
  ctx.restore()
}

function useDebounceEffect(
  fn,
  waitTime,
  deps,
) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps)
    }, waitTime)

    return () => {
      clearTimeout(t)
    }
  }, deps)
}
